import { useState, useEffect, useMemo } from 'react'
import {
  Modal,
  Typography,
  Stack,
  Button,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  Avatar,
  Checkbox,
  Divider,
  Box,
  CircularProgress,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Tooltip,
} from '@mui/joy'
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material'
import api from '../../services/api'

interface Contact {
  id: number
  name: string
  number: string
  profilePicUrl?: string
  urlPicture?: string
  isGroup?: boolean
}

export interface ForwardDestination {
  id: number
  name: string
  number: string
  isGroup: boolean
}

interface ForwardContactPickerProps {
  open: boolean
  onClose: () => void
  onForward: (contactIds: number[], destinations: ForwardDestination[]) => void
  loading: boolean
  /** Conexion del ticket de origen: hace falta para listar SUS grupos. */
  whatsappId?: number | null
  /** Canal del ticket: Meta no puede enviar a grupos. */
  channel?: string | null
}

type Pestana = 'contactos' | 'grupos'

export default function ForwardContactPicker({
  open,
  onClose,
  onForward,
  loading,
  whatsappId,
  channel,
}: ForwardContactPickerProps) {
  const [pestana, setPestana] = useState<Pestana>('contactos')
  const [contactos, setContactos] = useState<Contact[]>([])
  const [grupos, setGrupos] = useState<Contact[]>([])
  const [cargandoContactos, setCargandoContactos] = useState(false)
  const [cargandoGrupos, setCargandoGrupos] = useState(false)
  const [busquedaContactos, setBusquedaContactos] = useState('')
  const [busquedaGrupos, setBusquedaGrupos] = useState('')

  // Un solo mapa id -> destino. Guardar el objeto completo (y no solo el id) es
  // lo que permite que la seleccion sobreviva al cambio de pestana y a cada
  // busqueda: la lista visible se reemplaza, el mapa no.
  const [seleccionados, setSeleccionados] = useState<Record<number, ForwardDestination>>({})

  // Grupos: solo tienen sentido en WhatsApp (Baileys) y hace falta saber por que
  // conexion se envia, porque el numero propio debe ser miembro del grupo.
  const canal = String(channel || 'whatsapp').toLowerCase()
  const gruposHabilitados = canal === 'whatsapp' && !!whatsappId
  const motivoGruposBloqueados = !whatsappId
    ? 'Este ticket no tiene conexión de WhatsApp asignada'
    : `El canal ${canal} no permite enviar a grupos`

  useEffect(() => {
    if (!open) return
    setPestana('contactos')
    setSeleccionados({})
    setBusquedaContactos('')
    setBusquedaGrupos('')
  }, [open])

  // Antes se pedia `/contacts` sin parametros: el backend devuelve por defecto
  // solo 10 registros (ListContactsService rowsPerPage="10") y el buscador
  // filtraba EN MEMORIA sobre esos 10. Con 5.386 contactos eso dejaba fuera al
  // 99,8%, y los grupos —que se ordenan por nombre— no aparecian nunca.
  // Ahora cada pestana pide al servidor lo suyo: isGroup=false / isGroup=true.
  useEffect(() => {
    if (!open) return
    if (pestana === 'grupos' && !gruposHabilitados) return

    const esGrupos = pestana === 'grupos'
    const termino = esGrupos ? busquedaGrupos : busquedaContactos
    const fijarCarga = esGrupos ? setCargandoGrupos : setCargandoContactos
    const fijarLista = esGrupos ? setGrupos : setContactos

    let cancelado = false
    fijarCarga(true)

    // Se espera a que el usuario deje de teclear para no lanzar una peticion
    // por pulsacion.
    const temporizador = setTimeout(() => {
      api
        .get('/contacts', {
          params: {
            searchParam: termino || undefined,
            pageNumber: 1,
            rowsPerPage: 100,
            isGroup: esGrupos ? 'true' : 'false',
            // El filtro por conexion solo aplica a grupos: los contactos son
            // globales de la empresa y restringirlos esconderia destinos validos.
            whatsappId: esGrupos ? whatsappId : undefined,
          },
        })
        .then(({ data }) => {
          if (cancelado) return
          const lista = data?.contacts || data || []
          fijarLista(Array.isArray(lista) ? lista : [])
        })
        .catch(() => {
          if (!cancelado) fijarLista([])
        })
        .finally(() => {
          if (!cancelado) fijarCarga(false)
        })
    }, 350)

    return () => {
      cancelado = true
      clearTimeout(temporizador)
    }
  }, [open, pestana, busquedaContactos, busquedaGrupos, whatsappId, gruposHabilitados])

  // El servidor ya devuelve el resultado filtrado.
  const listaVisible = pestana === 'grupos' ? grupos : contactos
  const cargando = pestana === 'grupos' ? cargandoGrupos : cargandoContactos
  const busqueda = pestana === 'grupos' ? busquedaGrupos : busquedaContactos
  const fijarBusqueda = pestana === 'grupos' ? setBusquedaGrupos : setBusquedaContactos

  const idsSeleccionados = useMemo(
    () => Object.keys(seleccionados).map(Number),
    [seleccionados]
  )
  const totalContactos = useMemo(
    () => Object.values(seleccionados).filter(d => !d.isGroup).length,
    [seleccionados]
  )
  const totalGrupos = idsSeleccionados.length - totalContactos

  const alternarDestino = (contacto: Contact) => {
    setSeleccionados(prev => {
      const siguiente = { ...prev }
      if (siguiente[contacto.id]) {
        delete siguiente[contacto.id]
      } else {
        siguiente[contacto.id] = {
          id: contacto.id,
          name: contacto.name || contacto.number || `#${contacto.id}`,
          number: contacto.number || '',
          isGroup: !!contacto.isGroup || pestana === 'grupos',
        }
      }
      return siguiente
    })
  }

  // "Todos" opera solo sobre lo que se esta viendo: no debe tocar lo marcado en
  // la otra pestana ni en una busqueda anterior.
  const visiblesSeleccionados = listaVisible.filter(c => !!seleccionados[c.id]).length
  const todosVisiblesMarcados =
    listaVisible.length > 0 && visiblesSeleccionados === listaVisible.length

  const alternarTodosVisibles = () => {
    setSeleccionados(prev => {
      const siguiente = { ...prev }
      if (todosVisiblesMarcados) {
        listaVisible.forEach(c => delete siguiente[c.id])
      } else {
        listaVisible.forEach(c => {
          siguiente[c.id] = {
            id: c.id,
            name: c.name || c.number || `#${c.id}`,
            number: c.number || '',
            isGroup: !!c.isGroup || pestana === 'grupos',
          }
        })
      }
      return siguiente
    })
  }

  const handleSubmit = () => {
    if (idsSeleccionados.length === 0) return
    onForward(idsSeleccionados, Object.values(seleccionados))
  }

  const handleClose = () => {
    setBusquedaContactos('')
    setBusquedaGrupos('')
    setSeleccionados({})
    setContactos([])
    setGrupos([])
    onClose()
  }

  const textoVacio = (() => {
    if (busqueda) {
      return pestana === 'grupos' ? 'No se encontraron grupos' : 'No se encontraron contactos'
    }
    if (pestana === 'grupos') return 'Esta conexión no tiene grupos registrados'
    return 'No hay contactos'
  })()

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // Ancho fluido: evita desborde horizontal en pantallas angostas (iPhone SE/12)
          width: 'min(480px, calc(100vw - 32px))',
          maxHeight: '85vh',
          bgcolor: 'background.body',
          borderRadius: 'xl',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography level="title-md" fontWeight={700}>
            Reenviar mensaje
          </Typography>
          <IconButton size="sm" variant="plain" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Pestañas: contactos / grupos */}
        <Tabs
          value={pestana}
          onChange={(_, valor) => setPestana((valor as Pestana) || 'contactos')}
          sx={{ bgcolor: 'transparent' }}
        >
          <TabList sx={{ px: 1 }}>
            <Tab value="contactos" sx={{ flex: 1, gap: 0.75 }}>
              <PersonIcon sx={{ fontSize: 18 }} />
              Contactos
            </Tab>
            {gruposHabilitados ? (
              <Tab value="grupos" sx={{ flex: 1, gap: 0.75 }}>
                <GroupsIcon sx={{ fontSize: 18 }} />
                Grupos
              </Tab>
            ) : (
              <Tooltip title={motivoGruposBloqueados} variant="soft" placement="top">
                {/* El span recibe el hover: un Tab deshabilitado no dispara eventos
                    y el usuario se quedaria sin saber por que no puede entrar. */}
                <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                  <Tab value="grupos" disabled sx={{ flex: 1, gap: 0.75 }}>
                    <GroupsIcon sx={{ fontSize: 18 }} />
                    Grupos
                  </Tab>
                </Box>
              </Tooltip>
            )}
          </TabList>
        </Tabs>

        {/* Contenido */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
          {/* Buscador */}
          <Input
            placeholder={pestana === 'grupos' ? 'Buscar grupo...' : 'Buscar contacto...'}
            startDecorator={<SearchIcon sx={{ fontSize: 20 }} />}
            value={busqueda}
            onChange={(e) => fijarBusqueda(e.target.value)}
            autoFocus
            size="sm"
          />

          {/* Lista */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              borderRadius: 'sm',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : listaVisible.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                {pestana === 'grupos' ? (
                  <GroupsIcon sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 40, color: 'text.tertiary', mb: 1 }} />
                )}
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  {textoVacio}
                </Typography>
              </Box>
            ) : (
              <List size="sm" sx={{ p: 0 }}>
                {/* Header con contador y seleccionar todo */}
                <ListItem
                  endAction={
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={alternarTodosVisibles}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {todosVisiblesMarcados ? 'Deseleccionar' : 'Todos'}
                    </Button>
                  }
                  sx={{ minHeight: 36 }}
                >
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600 }}>
                    {pestana === 'grupos'
                      ? `${listaVisible.length} grupo${listaVisible.length !== 1 ? 's' : ''}`
                      : `${listaVisible.length} contacto${listaVisible.length !== 1 ? 's' : ''}`}
                  </Typography>
                </ListItem>
                <Divider />

                {listaVisible.map(contacto => (
                  <ListItem
                    key={contacto.id}
                    endAction={
                      <Checkbox
                        checked={!!seleccionados[contacto.id]}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => alternarDestino(contacto)}
                        size="sm"
                      />
                    }
                    sx={{ py: 0 }}
                  >
                    <ListItemButton onClick={() => alternarDestino(contacto)} sx={{ py: 0.75 }}>
                      <ListItemDecorator>
                        <Avatar
                          src={contacto.profilePicUrl || contacto.urlPicture}
                          size="sm"
                          sx={{ width: 36, height: 36 }}
                        >
                          {contacto.isGroup || pestana === 'grupos' ? (
                            <GroupsIcon sx={{ fontSize: 18 }} />
                          ) : (
                            contacto.name?.charAt(0)?.toUpperCase() || '?'
                          )}
                        </Avatar>
                      </ListItemDecorator>
                      <Stack spacing={0} sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography level="body-sm" sx={{ fontWeight: 600 }} noWrap>
                            {contacto.name}
                          </Typography>
                          {(contacto.isGroup || pestana === 'grupos') && (
                            <Typography
                              level="body-xs"
                              sx={{
                                px: 0.6,
                                borderRadius: 'sm',
                                bgcolor: 'primary.softBg',
                                color: 'primary.plainColor',
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              Grupo
                            </Typography>
                          )}
                        </Stack>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }} noWrap>
                          {contacto.number}
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Resumen de selección: incluye lo marcado en la OTRA pestaña */}
          {idsSeleccionados.length > 0 && (
            <Typography level="body-sm" sx={{ color: '#52b788', fontWeight: 600 }}>
              {idsSeleccionados.length} seleccionado{idsSeleccionados.length !== 1 ? 's' : ''}
              {' ('}
              {totalContactos} contacto{totalContactos !== 1 ? 's' : ''}
              {', '}
              {totalGrupos} grupo{totalGrupos !== 1 ? 's' : ''}
              {')'}
            </Typography>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          <Button variant="soft" color="neutral" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="solid"
            onClick={handleSubmit}
            loading={loading}
            disabled={idsSeleccionados.length === 0}
            startDecorator={loading ? undefined : <SendIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: '#52b788',
              '&:hover': { bgcolor: '#40916c' },
            }}
          >
            Reenviar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
